import React, { FC, memo, useCallback, useContext, useEffect, useState } from 'react'
import { isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'
import { generatePath, useHistory, useLocation } from 'react-router-dom'
import Cookies from 'js-cookie'
import {
  ActionSignatureStatus,
  LimitedAccessRight,
  useActionSignatureRequestsCountQuery,
  useCardsCountQuery,
  Individual,
  WalletStatus,
} from '../../graphql'
import { CardsTabsEnum, FacilitationTabsEnum } from '../../types'
import { CompanySelect } from './CompanySelect'
import { MainMenuItem } from './MainMenuItem'
import { APP_PATHS, PATH_PARAMS } from '../../routes/paths'
import { Footer } from '../Footer'
import config from '../../config'
import { useIsLimitedContract, useTotalUnreadDocCount } from '../../hooks'

import IconHome from '../../assets/images/icons/home_2.svg?react'
import AccountsIcon from '../../assets/images/icons/accounts-icon.svg?react'
import IconDevelopers from '../../assets/images/icons/developers.svg?react'
import IconPencil from '../../assets/images/icons/icon_pencil.svg?react'
import CompareArrows from '../../assets/images/icons/compare_arrows.svg?react'
import DirectDebitIcon from '../../assets/images/icons/direct-debit.svg?react'
import Payment from '../../assets/images/icons/payment.svg?react'
import IconClients from '../../assets/images/icons/people_alt.svg?react'
import PaymentFacilitationIcon from '../../assets/images/icons/payment-facilitation.svg?react'
import { EnableCryptoProductInfoModal } from '../Common/EnableCryptoProductInfoModal'
import CryptoIcon from '../../assets/images/icons/create-menu/crypto.svg?react'
import { CRYPTO_PRODUCT_STATUS } from '../../constants'
import { ConnectCryptoWalletButton } from '../Common/ConnectCryptoWalletButton'
import { useWeb3 } from '../../hooks/useWeb3'
import { currentStepVarPay } from '../../graphql/local/dashboard'
import { PaymentContext } from '../BatchPayment'
import { useReactiveVar } from '@apollo/client'
import { Box, Divider } from '@mui/material'
import {
  StyledCompanySelectWrap,
  StyledDrawerInnerCont,
  StyledList,
  StyledToolbar,
} from './StyledComponents'

const DashboardDrawerComponent: FC<{
  contractId: number | string | undefined
  setMobileOpen: (arg: boolean) => void
  user: Individual
  limitMode: boolean
  limitedAccessRight: LimitedAccessRight | null | undefined
  isIntroducerContractOwner: boolean
  hasCardPayment: boolean
  resetPages: () => void
  onLogoClick: () => void
}> = ({
  contractId,
  setMobileOpen,
  user,
  limitMode,
  limitedAccessRight,
  isIntroducerContractOwner,
  hasCardPayment,
  resetPages,
  onLogoClick,
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const { setIsNeedToReset } = useContext(PaymentContext)
  const [showCryptoModal, setShowCryptoModal] = useState(false)

  const { data: signaturesCount, refetch } = useActionSignatureRequestsCountQuery({
    variables: { contractId: contractId, statuses: [ActionSignatureStatus.Pending] },
    fetchPolicy: 'network-only',
  })

  const { refetchDocsCount } = useTotalUnreadDocCount()
  const { isLimitedContract, cryptoPaymentsEnabled, loading } = useIsLimitedContract()
  const { cryptoWallet } = useWeb3()

  const { data: cardsCount } = useCardsCountQuery({
    ...(contractId ? { variables: { contractId: +contractId } } : {}),
  })
  const paymentVarStep = useReactiveVar(currentStepVarPay)

  const contract = contractId ? user.contracts?.find((c) => c?.id === +contractId) : undefined
  const isIntroducingPartner = !!contract?.isIntroducingPartner
  const limitedGroupAccessArr = [
    LimitedAccessRight.CardHolder,
    LimitedAccessRight.PaymentFacilitationView,
  ]
  const paymentFacilitatorEnabled =
    config.isPaymentFacilitatorEnabled ||
    (import.meta.env.REACT_APP_PF_TEST_CONTRACTS || '').split(',').includes(String(contractId))

  useEffect(() => {
    void refetch()
    void refetchDocsCount()
  }, [location.pathname, refetch, refetchDocsCount])

  const handleHideCryptoButton = (requestSent: boolean) => {
    Cookies.set(`${CRYPTO_PRODUCT_STATUS}-${contractId}`, requestSent ? 'sent' : 'hide', {
      expires: 3650,
    })
    setShowCryptoModal(false)
  }

  const handleBuyCryptoItem = useCallback(() => {
    const isNewPaymentPath = location.pathname.includes('new-payment')
    currentStepVarPay(3)
    if (isNewPaymentPath) {
      setIsNeedToReset(true)
      history.replace(
        generatePath(APP_PATHS.dashboard.newPayment, {
          [PATH_PARAMS.applicationId]: contractId,
        }),
      )
    } else {
      history.push(
        generatePath(APP_PATHS.dashboard.newPayment, {
          [PATH_PARAMS.applicationId]: contractId,
        }),
      )
    }
  }, [location])

  return (
    <StyledDrawerInnerCont
      key={contractId}
      onClick={() => {
        setMobileOpen(false)
      }}
      justifyContent="space-between"
    >
      <Box>
        <StyledToolbar>
          <img src={config.logo} alt={'company_logo'} onClick={onLogoClick} />
        </StyledToolbar>

        <StyledCompanySelectWrap onClick={resetPages}>
          <CompanySelect contractId={contractId} />
        </StyledCompanySelectWrap>

        <StyledList onClick={resetPages}>
          {!limitedGroupAccessArr.includes(limitedAccessRight as LimitedAccessRight) &&
            !isIntroducerContractOwner && (
              <>
                <MainMenuItem
                  icon={<IconHome />}
                  primary={t('home', 'Home')}
                  path={generatePath(APP_PATHS.dashboard.home, {
                    [PATH_PARAMS.applicationId]: contractId,
                  })}
                  disabled={limitMode}
                  data-test="homePageNavItem"
                />

                <MainMenuItem
                  icon={<AccountsIcon />}
                  primary={t('accounts', 'Accounts')}
                  path={generatePath(APP_PATHS.dashboard.accounts, {
                    [PATH_PARAMS.applicationId]: contractId,
                  })}
                  disabled={limitMode}
                  data-test="autotest-accountsPageNavItem"
                />
              </>
            )}

          {isIntroducerContractOwner && (
            <MainMenuItem
              icon={<IconClients />}
              primary={t('home')}
              path={generatePath(APP_PATHS.dashboard.partnerManagementOverview, {
                [PATH_PARAMS.applicationId]: contractId,
              })}
              disabled={limitMode}
            />
          )}

          {!isIntroducerContractOwner &&
            limitedAccessRight !== LimitedAccessRight.PaymentFacilitationView && (
              <MainMenuItem
                icon={<CompareArrows />}
                primary={t('transactions', 'Transactions')}
                path={generatePath(APP_PATHS.dashboard.transactions, {
                  [PATH_PARAMS.applicationId]: contractId,
                })}
                disabled={limitMode}
                detailsPath={[APP_PATHS.dashboard.trxDetails, APP_PATHS.dashboard.cardTrxs]}
                data-test="drawer-transactions"
              />
            )}

          {!isIntroducerContractOwner &&
            !limitedGroupAccessArr.includes(limitedAccessRight as LimitedAccessRight) && (
              <MainMenuItem
                icon={<DirectDebitIcon />}
                primary={t('directDebit', 'Direct Debit')}
                path={generatePath(APP_PATHS.dashboard.directDebitMandatesOutbound, {
                  [PATH_PARAMS.applicationId]: contractId,
                  [PATH_PARAMS.accountId]: 'allAccounts',
                })}
                disabled={limitMode}
                detailsPath={[
                  APP_PATHS.dashboard.directDebitMandatesOutbound,
                  APP_PATHS.dashboard.directDebitMandatesOutboundRejected,
                  APP_PATHS.dashboard.directDebitMandatesInbound,
                  APP_PATHS.dashboard.directDebitMandatesInboundRejected,
                  APP_PATHS.dashboard.directDebitMandatesScheduledPayments,
                ]}
                data-test="direct-debits"
              />
            )}

          {(![LimitedAccessRight.ViewOnly, LimitedAccessRight.PaymentFacilitationView].includes(
            limitedAccessRight as LimitedAccessRight,
          ) ||
            (!!cardsCount?.contractCardsCount &&
              limitedAccessRight === LimitedAccessRight?.ViewOnly)) &&
            !isIntroducerContractOwner && (
              <MainMenuItem
                icon={<Payment />}
                primary={t('cards', 'Cards')}
                path={generatePath(APP_PATHS.dashboard.cards, {
                  [PATH_PARAMS.applicationId]: contractId,
                  [PATH_PARAMS.cardsTabNum]: CardsTabsEnum.virtual,
                })}
                disabled={limitMode}
                detailsPath={[
                  APP_PATHS.dashboard.activateCard,
                  APP_PATHS.dashboard.createCard,
                  APP_PATHS.dashboard.cards,
                ]}
                data-test="cardPageNavItem"
              />
            )}

          {!isIntroducerContractOwner &&
            !cryptoPaymentsEnabled &&
            ![...limitedGroupAccessArr, LimitedAccessRight?.ViewOnly].includes(
              limitedAccessRight as LimitedAccessRight,
            ) &&
            !loading && (
              <MainMenuItem
                icon={<CryptoIcon />}
                primary={t('stablecoinExchange', 'Stablecoin Exchange')}
                disabled={isLimitedContract || limitMode}
                onClick={() => setShowCryptoModal(true)}
              />
            )}

          {cryptoPaymentsEnabled &&
            ![...limitedGroupAccessArr, LimitedAccessRight?.ViewOnly].includes(
              limitedAccessRight as LimitedAccessRight,
            ) && (
              <ConnectCryptoWalletButton>
                {({ connect }) => (
                  <MainMenuItem
                    icon={<CryptoIcon />}
                    primary={t('cryptoWallet', 'Crypto Wallet')}
                    disabled={isLimitedContract || limitMode}
                    onClick={() => connect()}
                  />
                )}
              </ConnectCryptoWalletButton>
            )}
          {cryptoWallet?.status === WalletStatus.Active && (
            <MainMenuItem
              icon={<CryptoIcon />}
              primary={t('buySellCryptoCapital', 'Buy / Sell Crypto')}
              disabled={limitMode}
              onClick={handleBuyCryptoItem}
              path={
                paymentVarStep === 3
                  ? generatePath(APP_PATHS.dashboard.newPayment, {
                      [PATH_PARAMS.applicationId]: contractId,
                    })
                  : undefined
              }
            />
          )}
          {!limitedGroupAccessArr.includes(limitedAccessRight as LimitedAccessRight) &&
            !isIntroducerContractOwner && (
              <MainMenuItem
                icon={<IconClients />}
                primary={t('beneficiaries', 'Beneficiaries')}
                path={generatePath(APP_PATHS.dashboard.clients, {
                  [PATH_PARAMS.applicationId]: contractId,
                })}
                disabled={limitMode}
                detailsPath={APP_PATHS.dashboard.clientOverview}
              />
            )}

          {paymentFacilitatorEnabled && hasCardPayment && (
            <MainMenuItem
              icon={<PaymentFacilitationIcon />}
              primary={t('paymentFacilitator', 'Payment facilitation')}
              path={generatePath(APP_PATHS.dashboard.paymentFacilitator, {
                [PATH_PARAMS.applicationId]: contractId,
                [PATH_PARAMS.facilitationTab]: FacilitationTabsEnum.overview,
              })}
              detailsPath={[
                APP_PATHS.dashboard.paymentFacilitator,
                APP_PATHS.dashboard.paymentDetails,
              ]}
            />
          )}

          {isIntroducingPartner &&
            !isIntroducerContractOwner &&
            limitedAccessRight !== LimitedAccessRight.PaymentFacilitationView && (
              <MainMenuItem
                icon={<IconClients />}
                primary={t('partnerManagement', 'Partner management')}
                path={generatePath(APP_PATHS.dashboard.partnerManagementOverview, {
                  [PATH_PARAMS.applicationId]: contractId,
                })}
                disabled={limitMode}
              />
            )}

          {!limitedGroupAccessArr.includes(limitedAccessRight as LimitedAccessRight) &&
            !isIntroducerContractOwner && (
              <>
                <MainMenuItem
                  icon={<IconDevelopers />}
                  primary={t('developers', 'Developers')}
                  path={generatePath(APP_PATHS.dashboard.developers, {
                    [PATH_PARAMS.applicationId]: contractId,
                  })}
                  disabled={limitMode}
                  data-test="developersPageNavItem"
                />
              </>
            )}

          {![...limitedGroupAccessArr, LimitedAccessRight.ViewOnly].includes(
            limitedAccessRight as LimitedAccessRight,
          ) && (
            <>
              <Divider
                flexItem
                orientation="horizontal"
                sx={{
                  height: 0.125,
                  backgroundColor: '#E5E7EB',
                  my: 0.5,
                  mx: 0,
                }}
              />

              <MainMenuItem
                icon={<IconPencil />}
                countNum={signaturesCount?.actionSignatureRequestsCount || 0}
                primary={t('actions', 'Actions')}
                path={generatePath(APP_PATHS.dashboard.actions, {
                  [PATH_PARAMS.applicationId]: contractId,
                })}
                detailsPath={APP_PATHS.dashboard.actionDetails}
                resetCustomHistory
              />
            </>
          )}
        </StyledList>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Footer isMenu={true} />
      </Box>

      {showCryptoModal && (
        <EnableCryptoProductInfoModal
          isOpen={showCryptoModal}
          handleClose={handleHideCryptoButton}
          applicationId={contractId as string}
        />
      )}
    </StyledDrawerInnerCont>
  )
}

export const DashboardDrawer = memo(DashboardDrawerComponent, isEqual)
